import React from 'react';
import { formatAmount } from '../../datasets/utils/formatters';

interface FeeBreakdown {
  merchant: {
    fixed: number;
    percentage: number;
    beforeCap: number;
    afterCap: number;
    capApplied: boolean;
  };
  bank: {
    fixed: number;
    percentage: number;
    beforeCap: number;
    afterCap: number;
    capApplied: boolean;
  };
  profit: number;
}

interface QuoteBreakdownProps {
  quote: {
    id: string;
    name: string;
    bank?: {
      name: string;
      alias: string;
    };
    created_at: string;
    estimated_payments: number;
    estimated_payouts: number;
    average_amount: number;
    payment_approved_fixed_fee: number;
    payment_approved_percentage_fee: number;
    payment_approved_max_cap: number;
    payout_approved_fixed_fee: number;
    payout_approved_percentage_fee: number;
    payout_approved_max_cap: number;
  };
  breakdowns: {
    payment: FeeBreakdown;
    payout: FeeBreakdown;
  };
  bankFees: {
    getPaymentFees: (method: string, status: string) => any;
    getPayoutFees: (method: string, status: string) => any;
  };
}

export function QuoteBreakdown({ quote, breakdowns, bankFees }: QuoteBreakdownProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          {quote.name}
        </h2>
        <p className="text-sm text-gray-500">
          {quote.bank?.name} ({quote.bank?.alias})
        </p>
        <p className="text-sm text-gray-500">
          Created {new Date(quote.created_at).toLocaleDateString()}
        </p>
      </div>

      {/* Volume Information */}
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-500">Payment Volume</h3>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {formatAmount(quote.estimated_payments * quote.average_amount)}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {quote.estimated_payments.toLocaleString()} transactions × {formatAmount(quote.average_amount)}
          </p>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-500">Payout Volume</h3>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {formatAmount(quote.estimated_payouts * quote.average_amount)}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {quote.estimated_payouts.toLocaleString()} transactions × {formatAmount(quote.average_amount)}
          </p>
        </div>

        <div className="bg-indigo-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-indigo-900">Monthly Net Profit</h3>
          <p className="mt-1 text-2xl font-bold text-indigo-600">
            {formatAmount(
              (breakdowns.payment.profit || 0) +
              (breakdowns.payout.profit || 0)
            )}
          </p>
          <div className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between text-indigo-700">
              <span>Total Revenue</span>
              <span>{formatAmount(
                (breakdowns.payment.merchant.afterCap || 0) +
                (breakdowns.payout.merchant.afterCap || 0)
              )}</span>
            </div>
            <div className="flex justify-between text-indigo-700">
              <span>Total Bank Fees</span>
              <span>-{formatAmount(
                (breakdowns.payment.bank.afterCap || 0) +
                (breakdowns.payout.bank.afterCap || 0)
              )}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fee Breakdown */}
      <div className="space-y-4">
        <RevenueBreakdown
          title="Payment Revenue"
          breakdown={breakdowns.payment}
          quote={quote}
          type="payment"
          bankFees={bankFees}
        />
        <RevenueBreakdown
          title="Payout Revenue"
          breakdown={breakdowns.payout}
          quote={quote}
          type="payout"
          bankFees={bankFees}
        />
      </div>
    </div>
  );
}

interface RevenueBreakdownProps {
  title: string;
  breakdown: FeeBreakdown;
  quote: QuoteBreakdownProps['quote'];
  type: 'payment' | 'payout';
  bankFees: QuoteBreakdownProps['bankFees'];
}

function RevenueBreakdown({ title, breakdown, quote, type, bankFees }: RevenueBreakdownProps) {
  const isPayment = type === 'payment';
  const count = isPayment ? quote.estimated_payments : quote.estimated_payouts;
  const fixedFee = isPayment ? quote.payment_approved_fixed_fee : quote.payout_approved_fixed_fee;
  const percentageFee = isPayment ? quote.payment_approved_percentage_fee : quote.payout_approved_percentage_fee;
  const maxCap = isPayment ? quote.payment_approved_max_cap : quote.payout_approved_max_cap;
  const method = isPayment ? 'etransfer_request_money' : 'etransfer_send_money';
  const bankFee = isPayment 
    ? bankFees.getPaymentFees(method, 'approved')
    : bankFees.getPayoutFees(method, 'approved');

  return (
    <div className="bg-white border rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-900 mb-3">{title}</h4>
      <div className="space-y-4">
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">Merchant Fees</h5>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Fixed Fee</span>
              <span className="font-medium text-gray-900">
                {formatAmount(fixedFee)} × {count} = {formatAmount(breakdown.merchant.fixed)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Percentage Fee ({percentageFee}%)</span>
              <span className="font-medium text-gray-900">
                {formatAmount(breakdown.merchant.percentage)}
              </span>
            </div>
            <div className="flex justify-between font-medium">
              <span className="text-gray-600">Total Before Cap</span>
              <span className="text-gray-900">
                {formatAmount(breakdown.merchant.beforeCap)}
              </span>
            </div>
            {breakdown.merchant.capApplied && (
              <div className="flex justify-between text-amber-600">
                <span>Max Cap Applied ({formatAmount(maxCap)} per transaction)</span>
                <span>{formatAmount(breakdown.merchant.afterCap)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="pt-4 border-t">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Bank Fees</h5>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Fixed Fee</span>
              <span className="font-medium text-gray-900">
                {formatAmount(bankFee?.per_transaction_fee || 0)} × {count} = {formatAmount(breakdown.bank.fixed)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Percentage Fee ({bankFee?.rate_fee_percentage || 0}%)</span>
              <span className="font-medium text-gray-900">
                {formatAmount(breakdown.bank.percentage)}
              </span>
            </div>
            <div className="flex justify-between font-medium">
              <span className="text-gray-600">Total Before Cap</span>
              <span className="text-gray-900">
                {formatAmount(breakdown.bank.beforeCap)}
              </span>
            </div>
            {breakdown.bank.capApplied && (
              <div className="flex justify-between text-amber-600">
                <span>Max Cap Applied ({formatAmount(bankFee?.max_fee_cap || 0)} per transaction)</span>
                <span>{formatAmount(breakdown.bank.afterCap)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="pt-4 border-t">
          <div className="flex justify-between items-baseline">
            <h5 className="text-sm font-medium text-gray-700">Net Profit</h5>
            <span className="text-lg font-bold text-green-600">
              {formatAmount(breakdown.profit)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}